import { useEffect, useState } from 'react';
import { useAuth, useClerk, useSignIn } from '@clerk/clerk-react';

export const DEMO_HOST = 'demo.getrevtrack.com';
export const DEMO_USER_ID = 'user_3LOvmnPBo6cZ5DzdoPGJpCOpbaB';

export const isDemoHost = () =>
    typeof window !== 'undefined' && window.location.hostname === DEMO_HOST;

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

let correctionTask = null;

const sessionUserId = (session) => session?.publicUserData?.userId || session?.user?.id || null;

const findSession = (client, userId) => (client?.sessions || []).find(
    (session) => session.status === 'active' && sessionUserId(session) === userId
);

const signInToDemo = async (signIn, setActive) => {
    const response = await fetch(`https://${SERVER_URL}/api/demo-session`);
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(data.error || 'Could not open the demo');
    }
    const result = await signIn.create({
        strategy: 'ticket',
        ticket: data.token,
    });
    if (result.status !== 'complete' || !result.createdSessionId) {
        throw new Error('Demo sign-in did not finish');
    }
    await setActive({ session: result.createdSessionId });
};

const DemoAutoSignIn = () => {
    const isDemo = isDemoHost();
    const { isLoaded: authLoaded, isSignedIn, userId, sessionId } = useAuth();
    const { client, setActive, signOut } = useClerk();
    const { isLoaded: signInLoaded, signIn } = useSignIn();
    const [error, setError] = useState(null);
    const [attempt, setAttempt] = useState(0);

    const demoIsActive = userId === DEMO_USER_ID;
    const needsDemo = isDemo && !demoIsActive;
    const needsRealAccount = !isDemo && demoIsActive;

    useEffect(() => {
        if (!authLoaded || !signInLoaded) return undefined;
        if (!needsDemo && !needsRealAccount) {
            correctionTask = null;
            return undefined;
        }
        if (!signIn || !setActive) return undefined;

        let cancelled = false;

        if (!correctionTask) {
            correctionTask = (async () => {
                if (needsDemo) {
                    const demoSession = findSession(client, DEMO_USER_ID);
                    if (demoSession) {
                        await setActive({ session: demoSession.id });
                        return;
                    }
                    if (isSignedIn) {
                        await signOut();
                        return;
                    }
                    await signInToDemo(signIn, setActive);
                    return;
                }

                const realSession = (client?.sessions || []).find(
                    (session) => session.status === 'active' && sessionUserId(session) && sessionUserId(session) !== DEMO_USER_ID
                );
                if (realSession) {
                    await setActive({ session: realSession.id });
                    return;
                }
                await signOut(sessionId ? { sessionId } : undefined);
            })().finally(() => {
                correctionTask = null;
            });
        }

        correctionTask.catch((err) => {
            if (!cancelled) setError(err.message || 'Could not open the demo');
        });

        return () => {
            cancelled = true;
        };
    }, [authLoaded, signInLoaded, needsDemo, needsRealAccount, isSignedIn, signIn, setActive, signOut, client, sessionId, attempt]);

    if (!needsDemo && !needsRealAccount) return null;

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '24px',
        }}>
            <div style={{ textAlign: 'center', maxWidth: '360px' }}>
                <p style={{ margin: 0 }}>
                    {error || (needsDemo ? 'Opening the demo…' : 'Signing out of the demo…')}
                </p>
                {error && needsDemo && (
                    <button
                        type="button"
                        onClick={() => {
                            correctionTask = null;
                            setError(null);
                            setAttempt((value) => value + 1);
                        }}
                        style={{ marginTop: '16px' }}
                    >
                        Try again
                    </button>
                )}
            </div>
        </div>
    );
};

export default DemoAutoSignIn;
